import React from 'react';

import { IVoice } from '../../zustand-store/useAIVoicesStore';
import { useOrganisation } from '../../http-hooks/organisation/useOrganisation';
import { useToggleVoiceBookmark } from '../../http-hooks/ai-voices/useToggleVoiceBookmark';
import { VoiceRow } from './VoicesV2';
import { SkeletonRow } from '../skeletons/primitives';
import { C } from '../../styles/tokens';

interface BookmarkedVoicesListProps {
    org_id: string;
    onExplore?: () => void;
}

export const BookmarkedVoicesList: React.FC<BookmarkedVoicesListProps> = ({ org_id, onExplore }) => {
    const { data: organisation, isLoading } = useOrganisation({ org_id });
    const toggleVoiceBookmark = useToggleVoiceBookmark();

    const bookmarkedVoicesMap: Record<string, IVoice> = organisation?.bookmarked_voices ?? {};
    const bookmarkedVoices = Object.entries(bookmarkedVoicesMap);

    // Every voice in this list is bookmarked by definition — the star always
    // renders filled, and clicking it removes the bookmark.
    const saved = new Set(Object.keys(bookmarkedVoicesMap));

    if (isLoading) {
        return (
            <div style={{ background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13, overflow: 'hidden', marginBottom: 20 }}>
                {[0, 1, 2].map(i => (
                    <SkeletonRow key={i} thumbnailSize={38} thumbnailRadius={999} titleWidth="35%" subtitleWidth="20%" trailingWidth={70} borderBottom={i < 2} />
                ))}
            </div>
        );
    }

    if (bookmarkedVoices.length === 0) {
        return (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <div style={{
                    width: 60, height: 60, borderRadius: 16, background: C.n50, border: `1px solid ${C.n200}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
                }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={C.n500} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                    </svg>
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: C.n900, marginBottom: 6, letterSpacing: '-.01em' }}>
                    No bookmarks yet
                </h3>
                <p style={{ fontSize: 13.5, color: C.n500, marginBottom: 18, maxWidth: 380, margin: '0 auto 18px' }}>
                    Tap the star on any voice to save it here for quick access.
                </p>
                {onExplore && (
                    <button
                        onClick={onExplore}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px',
                            background: C.n900, color: C.w, border: 'none', borderRadius: 9,
                            fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        Browse the library
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.w} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                )}
            </div>
        );
    }

    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: C.n500, marginBottom: 14 }}>
                {bookmarkedVoices.length} saved voice{bookmarkedVoices.length === 1 ? '' : 's'}
            </div>
            <div style={{ background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13, overflow: 'hidden' }}>
                {bookmarkedVoices.map(([key, voice], i) => {
                    const isSaving = toggleVoiceBookmark.isLoading
                        && toggleVoiceBookmark.variables?.voice.code === voice.code
                        && toggleVoiceBookmark.variables?.voice.engine === voice.engine;

                    return (
                        <VoiceRow
                            key={key}
                            voice={voice}
                            index={i}
                            isLast={i === bookmarkedVoices.length - 1}
                            isPicker={false}
                            saved={saved}
                            isSaving={isSaving}
                            onSave={() => toggleVoiceBookmark.mutate({ org_id, voice })}
                            langName={voice.langCode}
                        />
                    );
                })}
            </div>
        </div>
    );
};
